import React, { useEffect, useState, useRef } from 'react'
import { Switch, Route, Redirect, useParams, Link } from "react-router-dom"

import useApiConnector from './js/hooks/ApiConnector';

import Layout from './js/components/Layout'
import DatacentreIndex from './js/components/DatacentreIndex'
import DatacentreDetail from './js/components/DatacentreDetail'
import DatacentreForm from './js/components/DatacentreForm'
import CabIndex from './js/components/CabIndex'
import CabDetail from './js/components/CabDetail'
import CabForm from './js/components/CabForm'
import UserIndex from './js/components/UserIndex'
import UserForm from './js/components/UserForm'
import GroupIndex from './js/components/GroupIndex'
import GroupForm from './js/components/GroupForm'
import SplashScreen from './js/components/SplashScreen'
import useActionLog from './js/hooks/ActionLog'


function App(props) {


  const appStates = {
    LOGIN: "Login",
    LOADING: "Loading",
    READY: "Ready"
  }

  const actionLog = useActionLog();
  const api = useApiConnector(actionLog);

  const [appState, setAppState] = useState(appStates.LOGIN);
  const [pageStatus, setPageStatus] = useState("");
  const [loginError, setLoginError] = useState("");

  const [user, setUser] = useState({
    loggedIn: false,
    username: null,
    accessLevel: 0,
    jwt: null
  });

  // Set to true when page first loads, used to check for JWT in localstorage
  const isInitialMount = useRef(true);

  // Log in using previous details
  function LoginRedirect() {
    let { token, key } = useParams();

    useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        console.log("params", token, key)
        api.getJWT(token, key).then(userObj => {
          console.log("logging in with ", userObj);
          setUser(userObj);
          localStorage.setItem('user', JSON.stringify(userObj));
        });
      }
    })
    if (user.jwt !== null) {
      return <Redirect to="/"></Redirect>
    } else {
      return <div>Requesting Login Token...</div>
    }
  }

  // Log in using SSO (after previous auth)
  function SSORedirect() {

    useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        api.getSSOToken().then(userObj => {
          console.log("got user object", userObj);
          if (!userObj.loggedIn) {
            setLoginError(userObj.error);
            setAppState(appStates.LOGIN);
            return;
          }
          console.log("logging in with ", userObj);
          setUser(userObj);
          localStorage.setItem('user', JSON.stringify(userObj));
        });
      }
    })

    if (user.jwt !== null) {
      return <Redirect to="/"></Redirect>
    } else {
      return <div>Requesting Login Token...</div>
    }
  }

  // Functions that can be passed to different components
  const appFns = {
    // initiate regular login (SSO is handled through a redirect)
    login: (params) => {
      setAppState(appStates.LOADING);
      api.localLogin(params.username, params.password).then(userData => {
        if (userData.error) {
          setLoginError(userData.error);
          setAppState(appStates.LOGIN);
        } else {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
        setPageStatus("");
      });
      setPageStatus(status => {
        return "Logging In...";
      })
    },
    logout: () => {
      setUser({
        loggedIn: false,
        username: null,
        accessLevel: 0,
        jwt: null
      });
      localStorage.removeItem('user');
      api.logout();
      window.location.href = '/';
    }
  }

  useEffect(() => {
    // Check for stored user credentials
    if (!user.loggedIn) {
      let storedUser = JSON.parse(localStorage.getItem('user'));
      if (storedUser != null) {
        setUser(storedUser);
        api.setJWT(storedUser.jwt);
      }
    }
    // If we are already logged in or we are on the login page set state to loading
    if (user.loggedIn || window.location.pathname.indexOf('/login') !== -1) {
      setAppState(appStates.LOADING);
    } else {
      setAppState(appStates.LOGIN);
    }

    // If we are logged in and the page is not ready query datacentres
    if (user.loggedIn && appState != appStates.READY) {
      setPageStatus(status => {
        return "Fetching Data...";
      })
      api.getDataCentres().then(res => {
        setPageStatus(status => {
          return "";
        })
        setAppState(appStates.READY);
      }).catch(err => {
        // If fetching datacentres fails...
        if (!err.response) {
          // If no response then display an error message
          setPageStatus(status => {
            return "No Response from server, please try again later"
          });
        } else {
          // If we get a 401 or 403 then force re-auth
          if (err.response.status === 401 || err.response.status === 403) {
            // Update to show not logged in so we don't continue requesting pages
            // Don't use logout function because we want to show the error on the login page
            setUser(user => {
              user.loggedIn = false;
              return user;
            })
            setAppState(appStates.LOGIN);
            setLoginError("Session expired, please log in again.")
          }
          // If we get a 500 then show an error
          if (err.response.status === 500) {
            setPageStatus(status => {
              return "Internal server error, please try again later"
            });
          }
        }
      });
      // If the user is an admin query the groups list
      if (user.accessLevel >= 2) {
        api.getGroups();
      }
    }
    // Repeat this function whenever the current user changes
  }, [user]);

  useEffect(() => {
    console.log(user);
  }, [user]);

  return <Layout appFns={appFns} user={user} appState={appState}>
    <Switch>
      <Route exact path="/">
        <div className="row">
          {api.data.datacentres && api.data.datacentres.map(d => 
            <DatacentreIndex key={"dc_" + d.id} dc={d} user={user} api={api}>
              {d.cabs.map(c => 
                <CabIndex key={"cab_" + c.id} cab={c} dcId={d.id} user={user} api={api}>
                </CabIndex>
              )}
            </DatacentreIndex>
          )}
          {user.accessLevel >= 2 &&
            <div className={`Datacentre col-md-4 col-sm-12 ${user.accessLevel < 2 ? 'd-none' : ''}`}>
              <div className="list-group">
                <div className="Datacentre-heading list-group-item"><Link className="Datacentre-link" to={"/datacentre/new"}>Add new Datacentre...</Link></div>
              </div>
            </div>
          }
        </div>
      </Route>
      <Route path="/login/:token/:key">
        <LoginRedirect></LoginRedirect>
      </Route>
      <Route exact path="/sso">
        <SSORedirect></SSORedirect>
      </Route>
      <Route exact path="/datacentre/new" children={<DatacentreForm api={api} user={user} />} />
      <Route exact path="/datacentre/:dcId" children={<DatacentreDetail api={api} user={user} />} />
      <Route exact path="/datacentre/:dcId/cab/new" children={<CabForm api={api} user={user} />} />
      <Route exact path="/datacentre/:dcId/cab/:cabId" children={<CabDetail api={api} user={user} />} />
      <Route exact path="/users" children={<UserIndex api={api} user={user} />} />
      <Route exact path="/user/new" children={<UserForm api={api} user={user} cancelFunction={() => window.location = '/users'} submitFunction={api.createUser} />} />
      <Route exact path="/groups" children={<GroupIndex api={api} user={user} />} />
      <Route exact path="/group/new" children={<GroupForm api={api} user={user} />} />
    </Switch>
    {actionLog.display()}
    <SplashScreen appState={appState} appStates={appStates} appFns={appFns} loginError={loginError} pageStatus={pageStatus}></SplashScreen>
  </Layout>
}

export default App
